import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: string;
              size?: string;
              width?: number;
              text?: string;
              shape?: string;
              locale?: string;
            }
          ) => void;
        };
      };
    };
  }
}

interface GoogleLoginButtonProps {
  width?: number;
}

export function GoogleLoginButton({ width = 360 }: GoogleLoginButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        await googleLogin(response.credential);
        navigate('/library', { replace: true });
      } catch {
        // Error is already handled by the store
      }
    },
    [googleLogin, navigate]
  );

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google || !buttonRef.current) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
    });

    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: 'filled_black',
      size: 'large',
      width,
      text: 'continue_with',
      shape: 'pill',
      locale: 'pt-BR',
    });
  }, [handleCredentialResponse, width]);

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const enabled = import.meta.env.VITE_FF_GOOGLE_AUTH === 'true';
  if (!clientId || !enabled) return null;

  return <div ref={buttonRef} className="flex justify-center" />;
}
